import type { Tab } from './index';
import { renderMarkdown, esc } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';

interface ResearchResponse {
  ticker:    string;
  report:    string;
  timestamp: string;
  sections?: {
    technical?:   string;
    fundamental?: string;
    sentiment?:   string;
  };
  savedToNotes?: boolean;
  error?:        string;
}

const AGENTS = ['tech', 'fund', 'sent'] as const;

export class DeepResearchTab implements Tab {
  readonly id = 'research';

  private lastReport: ResearchResponse | null = null;

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }

  private input(): HTMLInputElement {
    return document.getElementById('dr-ticker') as HTMLInputElement;
  }

  init(): void {
    // Run button + Enter key
    this.el('dr-run-btn').addEventListener('click', () => this.run());
    this.input().addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') this.run();
    });

    // Example ticker chips
    this.el('dr-empty').querySelectorAll<HTMLElement>('[data-ticker]').forEach(chip => {
      chip.addEventListener('click', () => {
        this.input().value = chip.dataset.ticker ?? '';
        this.run();
      });
    });

    // Report action buttons
    this.el('dr-copy-btn').addEventListener('click',        () => this.copy());
    this.el('dr-save-notes-btn').addEventListener('click',  () => this.saveToNotes());
    this.el('dr-new-search-btn').addEventListener('click',  () => this.reset());
    this.el('dr-spec-toggle').addEventListener('click',     () => this.toggleSpecialists());
  }

  render(): void {
    // No auto-fetch — user drives research
  }

  private reset(): void {
    this.el('dr-output').style.display = 'none';
    this.el('dr-agents').style.display = 'none';
    this.el('dr-empty').style.display  = '';
    this.input().value = '';
    this.input().focus();
  }

  private toggleSpecialists(): void {
    const panel = this.el('dr-specialists');
    const btn   = this.el('dr-spec-toggle');
    const open  = panel.style.display !== 'none';
    panel.style.display = open ? 'none' : 'block';
    btn.textContent = (open ? '▶ Show' : '▼ Hide') + ' specialist reports';
  }

  private copy(): void {
    if (!this.lastReport?.report) return;
    const btn = this.el('dr-copy-btn');
    navigator.clipboard.writeText(this.lastReport.report)
      .then(() => {
        const orig = btn.textContent ?? '';
        btn.textContent = '✓ COPIED';
        setTimeout(() => { btn.textContent = orig; }, 1500);
      })
      .catch(() => {});
  }

  private async saveToNotes(): Promise<void> {
    if (!this.lastReport) return;
    const btn = this.el('dr-save-notes-btn') as HTMLButtonElement;
    btn.disabled    = true;
    btn.textContent = '⟳ Saving…';
    try {
      await workerFetch('/api/notes', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          ticker:  this.lastReport.ticker,
          title:   `${this.lastReport.ticker} — Deep Research ${new Date().toISOString().substring(0, 10)}`,
          content: this.lastReport.report,
        }),
      });
      this.el('dr-saved-badge').style.display = '';
      btn.textContent = '✓ Saved';
      showToast(`✓ ${this.lastReport.ticker} research saved to Notes`);
    } catch (err: unknown) {
      showToast(`Save failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
      btn.disabled    = false;
      btn.textContent = '💾 Save to Notes';
    }
  }

  private async run(): Promise<void> {
    const ticker = this.input().value.trim().toUpperCase().replace(/[^A-Z.]/g, '');
    if (!ticker) { this.input().focus(); return; }


    const runBtn = this.el('dr-run-btn') as HTMLButtonElement;

    // Reset UI state
    this.el('dr-empty').style.display   = 'none';
    this.el('dr-output').style.display  = 'none';
    this.el('dr-error-msg').innerHTML   = '';
    this.el('dr-specialists').style.display = 'none';
    this.el('dr-spec-toggle').textContent   = '▶ Show specialist reports';

    for (const k of [...AGENTS, 'synth'] as const) {
      const status = this.el(`dr-status-${k}`);
      const card   = this.el(`dr-agent-${k}`);
      status.textContent = k === 'synth' ? 'Waiting…' : 'Running…';
      status.className   = k === 'synth' ? 'dr-agent-status' : 'dr-agent-status running';
      card.classList.remove('done', 'error');
    }

    this.el('dr-agents').style.display = '';
    runBtn.disabled    = true;
    runBtn.textContent = '⟳ RUNNING…';

    // Staggered cosmetic hints
    const hints: ReturnType<typeof setTimeout>[] = [
      setTimeout(() => { const e = this.el('dr-status-tech');  if (e.textContent === 'Running…') e.textContent = 'Analyzing…'; }, 7000),
      setTimeout(() => { const e = this.el('dr-status-fund');  if (e.textContent === 'Running…') e.textContent = 'Analyzing…'; }, 10000),
      setTimeout(() => { const e = this.el('dr-status-sent');  if (e.textContent === 'Running…') e.textContent = 'Analyzing…'; }, 13000),
      setTimeout(() => { const e = this.el('dr-status-synth'); if (e.textContent === 'Waiting…') e.textContent = 'Synthesizing…'; }, 20000),
    ];

    try {
      const data = await workerFetch<ResearchResponse>('/api/research', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ ticker }),
        signal:  AbortSignal.timeout(90000), // 90s — 3 parallel agents + synthesis
      });
      hints.forEach(clearTimeout);
      if (data.error) throw new Error(data.error);

      // Staggered "Done" reveals
      AGENTS.forEach((k, i) => {
        setTimeout(() => {
          const s = this.el(`dr-status-${k}`);
          const c = this.el(`dr-agent-${k}`);
          s.textContent = '✓ Done';
          s.className   = 'dr-agent-status done';
          c.classList.add('done');
        }, i * 120);
      });

      setTimeout(() => {
        const synthS = this.el('dr-status-synth');
        const synthC = this.el('dr-agent-synth');
        synthS.textContent = '✓ Done';
        synthS.className   = 'dr-agent-status done';
        synthC.classList.add('done');
        setTimeout(() => this.showReport(data), 500);
      }, 420);

    } catch (err: unknown) {
      hints.forEach(clearTimeout);
      const msg = err instanceof Error ? err.message : String(err);
      for (const k of [...AGENTS, 'synth'] as const) {
        const s = this.el(`dr-status-${k}`);
        const c = this.el(`dr-agent-${k}`);
        s.textContent = 'Error';
        s.className   = 'dr-agent-status error';
        c.classList.remove('done');
        c.classList.add('error');
      }
      this.el('dr-error-msg').innerHTML =
        `<div style="margin-top:12px;padding:12px 16px;background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.3);border-radius:3px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    } finally {
      runBtn.disabled    = false;
      runBtn.textContent = '▶ RUN RESEARCH';
    }
  }

  private showReport(data: ResearchResponse): void {
    this.lastReport = data;

    this.el('dr-agents').style.display = 'none';
    this.el('dr-output').style.display = '';

    this.el('dr-report-ticker').textContent = data.ticker;
    this.el('dr-report-ts').textContent     = new Date(data.timestamp).toLocaleString();
    this.el('dr-saved-badge').style.display = 'none'; // reset — user saves manually

    // Reset save button for new report
    const saveBtn = this.el('dr-save-notes-btn') as HTMLButtonElement;
    saveBtn.disabled    = false;
    saveBtn.textContent = '💾 Save to Notes';

    this.el('dr-report-body').innerHTML  = renderMarkdown(data.report ?? '', 'dr-table');

    if (data.sections) {
      this.el('dr-spec-tech').innerHTML = renderMarkdown(data.sections.technical  ?? '', 'dr-table');
      this.el('dr-spec-fund').innerHTML = renderMarkdown(data.sections.fundamental ?? '', 'dr-table');
      this.el('dr-spec-sent').innerHTML = renderMarkdown(data.sections.sentiment  ?? '', 'dr-table');
    }

    this.el('dr-output').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
